import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Upload, Download, Trash2, Pencil, RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';
import { api, downloadBlob } from '@/api/client';
import { MultiSelectFilter } from '@/components/ui/MultiSelectFilter';
import { GmailPagination } from '@/components/ui/GmailPagination';
import {
  Lead, RATINGS, SERVICE_TYPES, dateOnly, dateTime, money, ratingClass, useLeadMasters, useLeadUsers,
} from './leads.api';
import { LeadContactButtons } from './LeadContactButtons';
import { LeadFormModal } from './LeadFormModal';
import { LeadDetailDrawer } from './LeadDetailDrawer';
import { LeadImportDialog } from './LeadImportDialog';
import { LeadsSubNav } from './LeadsSubNav';

/**
 * Manage Leads — the working list.
 *
 * Columns are the logistics ones a rate conversation turns on (volume, service, city, current
 * courier) rather than a generic contact grid, plus whatever custom columns Settings has added.
 * Call and WhatsApp sit on the row itself: the whole point of the screen is that someone picks a
 * row and contacts them, and making that two clicks deeper is how a list stops being worked.
 */
export function LeadsListPage() {
  const qc = useQueryClient();
  const { data: masters } = useLeadMasters();
  const { data: users } = useLeadUsers();

  const [q, setQ] = useState('');
  const [statusIds, setStatusIds] = useState<(string | number)[]>([]);
  const [sourceIds, setSourceIds] = useState<(string | number)[]>([]);
  const [ownerIds, setOwnerIds] = useState<(string | number)[]>([]);
  const [tagIds, setTagIds] = useState<(string | number)[]>([]);
  const [ratings, setRatings] = useState<(string | number)[]>([]);
  const [serviceTypes, setServiceTypes] = useState<(string | number)[]>([]);
  const [city, setCity] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [followup, setFollowup] = useState('');
  const [converted, setConverted] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const [addOpen, setAddOpen] = useState(false);
  const [editLead, setEditLead] = useState<Lead | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);

  const params = useMemo(() => {
    const p: any = { page, pageSize, sortBy, sortDir };
    if (q) p.q = q;
    if (statusIds.length) p.statusIds = statusIds.join(',');
    if (sourceIds.length) p.sourceIds = sourceIds.join(',');
    if (ownerIds.length) p.ownerIds = ownerIds.join(',');
    if (tagIds.length) p.tagIds = tagIds.join(',');
    if (ratings.length) p.ratings = ratings.join(',');
    if (serviceTypes.length) p.serviceTypes = serviceTypes.join(',');
    if (city) p.city = city;
    if (fromDate) p.fromDate = fromDate;
    if (toDate) p.toDate = toDate;
    if (followup) p.followup = followup;
    if (converted) p.converted = converted;
    return p;
  }, [q, statusIds, sourceIds, ownerIds, tagIds, ratings, serviceTypes, city, fromDate, toDate, followup, converted, sortBy, sortDir, page, pageSize]);

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['leads', params],
    queryFn: async () => (await api.get<{ total: number; items: Lead[] }>('/leads', { params })).data,
  });

  const del = useMutation({
    mutationFn: async (v: { id: number; reason: string }) => (await api.delete(`/leads/${v.id}`, { params: { reason: v.reason } })).data,
    onSuccess: () => {
      toast.success('Moved to Trash — restore it from the Trash tab if that was a mistake');
      qc.invalidateQueries({ queryKey: ['leads'] });
      qc.invalidateQueries({ queryKey: ['lead-dashboard'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not delete'),
  });

  const resetPage = () => setPage(1);
  const listCustom = (masters?.fields ?? []).filter((f) => f.isActive && f.showInList);

  const handleExport = async () => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (k !== 'page' && k !== 'pageSize') qs.set(k, String(v)); });
    await downloadBlob(`/leads/export?${qs.toString()}`, `leads-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const sortHeader = (key: string, label: string) => (
    <th className="text-left px-3 py-2 cursor-pointer select-none whitespace-nowrap"
      onClick={() => { setSortBy(key); setSortDir(sortBy === key && sortDir === 'desc' ? 'asc' : 'desc'); resetPage(); }}>
      {label}{sortBy === key ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ''}
    </th>
  );

  const items = data?.items ?? [];

  return (
    <div className="p-6">
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">Manage Leads</h1>
          <p className="text-xs text-gray-500 mt-0.5">
            {data?.total?.toLocaleString('en-IN') ?? 0} leads. Call or WhatsApp straight from a row — both are logged against the lead.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => refetch()} className="px-2 py-2 border rounded text-sm" title="Refresh">
            <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
          </button>
          <button onClick={() => setImportOpen(true)} className="px-3 py-2 border border-emerald-600 text-emerald-700 rounded text-sm inline-flex items-center gap-1">
            <Upload size={14} /> Import
          </button>
          <button onClick={handleExport} className="px-3 py-2 border border-emerald-600 text-emerald-700 rounded text-sm inline-flex items-center gap-1">
            <Download size={14} /> Export
          </button>
          <button onClick={() => setAddOpen(true)} className="px-3 py-2 bg-emerald-600 text-white rounded text-sm inline-flex items-center gap-1 hover:bg-emerald-700">
            <Plus size={14} /> Add Lead
          </button>
        </div>
      </div>

      <LeadsSubNav />

      {/* filters */}
      <div className="bg-white dark:bg-slate-900 border rounded p-3 mb-4 grid grid-cols-2 md:grid-cols-6 gap-3">
        <div className="col-span-2">
          <label className="text-xs text-gray-500">Search</label>
          <div className="relative">
            <Search size={13} className="absolute left-2 top-2.5 text-gray-400" />
            <input className="w-full border rounded pl-7 pr-2 py-1.5 text-sm dark:bg-slate-900"
              placeholder="Company, contact, mobile, e-mail, lead no"
              value={q} onChange={(e) => { setQ(e.target.value); resetPage(); }} />
          </div>
        </div>
        <div>
          <label className="text-xs text-gray-500">Status</label>
          <MultiSelectFilter label="" placeholder="All" values={statusIds} onChange={(v) => { setStatusIds(v); resetPage(); }}
            options={(masters?.statuses ?? []).map((s) => ({ value: s.id, label: s.name }))} />
        </div>
        <div>
          <label className="text-xs text-gray-500">Source</label>
          <MultiSelectFilter label="" placeholder="All" values={sourceIds} onChange={(v) => { setSourceIds(v); resetPage(); }}
            options={(masters?.sources ?? []).map((s) => ({ value: s.id, label: s.name }))} />
        </div>
        <div>
          <label className="text-xs text-gray-500">Sale Person</label>
          <MultiSelectFilter label="" placeholder="All" values={ownerIds} onChange={(v) => { setOwnerIds(v); resetPage(); }}
            options={[{ value: -1, label: 'Unassigned' }, ...(users ?? []).map((u) => ({ value: u.id, label: u.name }))]} />
        </div>
        <div>
          <label className="text-xs text-gray-500">Tags</label>
          <MultiSelectFilter label="" placeholder="All" values={tagIds} onChange={(v) => { setTagIds(v); resetPage(); }}
            options={(masters?.tags ?? []).map((t) => ({ value: t.id, label: t.name }))} />
        </div>
        <div>
          <label className="text-xs text-gray-500">Rating</label>
          <MultiSelectFilter label="" placeholder="All" values={ratings} onChange={(v) => { setRatings(v); resetPage(); }}
            options={RATINGS.map((r) => ({ value: r, label: r }))} />
        </div>
        <div>
          <label className="text-xs text-gray-500">Service</label>
          <MultiSelectFilter label="" placeholder="All" values={serviceTypes} onChange={(v) => { setServiceTypes(v); resetPage(); }}
            options={SERVICE_TYPES.map((r) => ({ value: r, label: r }))} />
        </div>
        <div>
          <label className="text-xs text-gray-500">City</label>
          <input className="w-full border rounded px-2 py-1.5 text-sm dark:bg-slate-900" value={city} onChange={(e) => { setCity(e.target.value); resetPage(); }} />
        </div>
        <div>
          <label className="text-xs text-gray-500">Follow-up</label>
          <select className="w-full border rounded px-2 py-1.5 text-sm dark:bg-slate-900" value={followup} onChange={(e) => { setFollowup(e.target.value); resetPage(); }}>
            <option value="">Any</option>
            <option value="due">Has one planned</option>
            <option value="overdue">Overdue</option>
            <option value="none">Nothing planned</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500">Signed up</label>
          <select className="w-full border rounded px-2 py-1.5 text-sm dark:bg-slate-900" value={converted} onChange={(e) => { setConverted(e.target.value); resetPage(); }}>
            <option value="">Any</option>
            <option value="yes">Converted</option>
            <option value="no">Not yet</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500">Added from</label>
          <input type="date" className="w-full border rounded px-2 py-1.5 text-sm dark:bg-slate-900" value={fromDate} onChange={(e) => { setFromDate(e.target.value); resetPage(); }} />
        </div>
        <div>
          <label className="text-xs text-gray-500">Added to</label>
          <input type="date" className="w-full border rounded px-2 py-1.5 text-sm dark:bg-slate-900" value={toDate} onChange={(e) => { setToDate(e.target.value); resetPage(); }} />
        </div>
      </div>

      <div className="bg-white dark:bg-slate-900 border rounded overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-emerald-50 dark:bg-slate-800 text-xs uppercase text-emerald-900 dark:text-slate-200">
              <tr>
                {sortHeader('leadNo', 'Lead No')}
                {sortHeader('companyName', 'Company')}
                <th className="text-left px-3 py-2">Contact</th>
                <th className="text-left px-3 py-2">Reach</th>
                {sortHeader('city', 'City')}
                <th className="text-left px-3 py-2">Service</th>
                {sortHeader('monthlyOrders', 'Monthly Orders')}
                <th className="text-left px-3 py-2">Current Courier</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Source</th>
                <th className="text-left px-3 py-2">Sale Person</th>
                <th className="text-left px-3 py-2">Rating</th>
                {sortHeader('estimatedValue', 'Est. Value')}
                {sortHeader('nextFollowupAt', 'Next Follow-up')}
                <th className="text-left px-3 py-2">Tags</th>
                {listCustom.map((cf) => <th key={cf.id} className="text-left px-3 py-2 whitespace-nowrap">{cf.label}</th>)}
                {sortHeader('createdAt', 'Added')}
                <th className="text-left px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={18 + listCustom.length} className="text-center py-8 text-gray-400">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={18 + listCustom.length} className="text-center py-8 text-gray-400">
                  No leads match these filters. Add one, or import a list.
                </td></tr>
              ) : items.map((l) => (
                <tr key={l.id} className="border-t hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer" onClick={() => setDetailId(l.id)}>
                  <td className="px-3 py-2 font-mono text-xs text-gray-500 whitespace-nowrap">{l.leadNo}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium text-slate-800 dark:text-slate-100">{l.companyName}</div>
                    {l.brandName && <div className="text-[11px] text-gray-400">{l.brandName}</div>}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <div>{l.contactName || '—'}</div>
                    <div className="text-gray-400">{l.mobile || l.email || ''}</div>
                  </td>
                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                    <LeadContactButtons leadId={l.id} mobile={l.mobile} company={l.companyName} contactName={l.contactName}
                      onDone={() => qc.invalidateQueries({ queryKey: ['leads'] })} />
                  </td>
                  <td className="px-3 py-2 text-xs">{l.city || '—'}</td>
                  <td className="px-3 py-2 text-xs">{l.serviceType || '—'}</td>
                  <td className="px-3 py-2 text-xs tabular-nums">{l.monthlyOrders?.toLocaleString('en-IN') ?? '—'}</td>
                  <td className="px-3 py-2 text-xs">{l.currentCourier || '—'}</td>
                  <td className="px-3 py-2">
                    {l.status ? (
                      <span className="text-[11px] px-2 py-0.5 rounded-full border whitespace-nowrap"
                        style={{ borderColor: l.status.colour ?? '#cbd5e1', color: l.status.colour ?? '#475569' }}>{l.status.name}</span>
                    ) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-xs">{l.source?.name || '—'}</td>
                  <td className={`px-3 py-2 text-xs ${l.ownerName ? '' : 'text-gray-400 italic'}`}>{l.ownerName || 'Unassigned'}</td>
                  <td className="px-3 py-2">
                    {l.rating ? <span className={`text-[10px] px-2 py-0.5 rounded-full border ${ratingClass(l.rating)}`}>{l.rating}</span> : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-xs tabular-nums">{l.estimatedValue ? money(l.estimatedValue) : '—'}</td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">
                    {l.nextFollowupAt ? (
                      <span className={new Date(l.nextFollowupAt) < new Date() ? 'text-rose-600 font-medium' : 'text-slate-600'}>
                        {dateTime(l.nextFollowupAt)}
                      </span>
                    ) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-0.5 max-w-[160px]">
                      {(l.tags ?? []).map((t) => (
                        <span key={t.id} className="text-[10px] px-1.5 py-0.5 rounded-full border whitespace-nowrap"
                          style={{ borderColor: t.colour ?? '#cbd5e1', color: t.colour ?? '#475569' }}>{t.name}</span>
                      ))}
                    </div>
                  </td>
                  {listCustom.map((cf) => {
                    const v = (l.customFields ?? {})[cf.key];
                    return <td key={cf.id} className="px-3 py-2 text-xs">{v === true ? 'Yes' : v === false || v === undefined || v === null || v === '' ? '—' : String(v)}</td>;
                  })}
                  <td className="px-3 py-2 text-xs whitespace-nowrap">{dateOnly(l.createdAt)}</td>
                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setEditLead(l)} className="p-1 border rounded hover:bg-slate-50" title="Edit"><Pencil size={12} /></button>
                      <button
                        onClick={() => {
                          const reason = window.prompt(`Move ${l.companyName} to Trash?\n\nWhy? (optional — helps whoever reviews the bin)`, '');
                          if (reason === null) return;
                          del.mutate({ id: l.id, reason });
                        }}
                        className="p-1 border rounded text-rose-600 border-rose-200 hover:bg-rose-50" title="Move to Trash">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-3 py-2 border-t">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span>Rows:</span>
            <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); resetPage(); }} className="border rounded px-1 py-0.5 dark:bg-slate-900">
              {[25, 50, 100, 200, 500].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <GmailPagination page={page} pageSize={pageSize} total={data?.total ?? 0} onPage={setPage} />
        </div>
      </div>

      {addOpen && <LeadFormModal onClose={() => setAddOpen(false)} />}
      {editLead && <LeadFormModal lead={editLead} onClose={() => setEditLead(null)} />}
      {importOpen && <LeadImportDialog onClose={() => setImportOpen(false)} />}
      {detailId && (
        <LeadDetailDrawer leadId={detailId} onClose={() => setDetailId(null)}
          onEdit={(l) => { setDetailId(null); setEditLead(l); }} />
      )}
    </div>
  );
}
